// CreateImages.jsx - VERSIÓN CON MENÚ INTEGRADO (CON BOTÓN "CANCELAR" EN PANEL)
import React, { useEffect, useState, useCallback } from 'react';
import './CreateImages.css';
import './HomeAdmin.css';
import zmLogo from "./images/LOGO_ZM.png";
import { useParams } from "react-router-dom";
import AdminNavbar from './AdminNavbar';

/**
 * Reemplaza según tu backend
 */
const API_URL = 'https://backend-zonamicroondas.vercel.app/api';
const NEWS_PAGE_SIZE = 5; // <--- mostrar 5 noticias por página en la lista

export default function CreateImages() {
  const { id: newsIdFromRoute } = useParams();

  /* ====== ESTADOS ====== */
  const [newsList, setNewsList] = useState([]);
  const [selectedNewsId, setSelectedNewsId] = useState(newsIdFromRoute ? String(newsIdFromRoute) : '');
  const [images, setImages] = useState([]);

  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState({
    image: null,
    caption: '',
    alt_text: '',
    position: 0
  });

  // PAGINACIÓN para la lista de noticias
  const [page, setPage] = useState(0);

  /* --------------------- Cargar lista de noticias --------------------- */
  const loadNews = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/news`);
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Error HTTP ${res.status}: ${errorText}`);
      }
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Error cargando noticias');

      const fullList = Array.isArray(json.data) ? json.data : [];
      setNewsList(fullList);

      // si ruta trae id, intentar posicionar la página para mostrarla
      if (newsIdFromRoute) {
        const idx = fullList.findIndex(n => String(n.id) === String(newsIdFromRoute));
        if (idx >= 0) {
          setPage(Math.floor(idx / NEWS_PAGE_SIZE));
          setSelectedNewsId(String(newsIdFromRoute));
        }
      }
    } catch (err) {
      setError('No se pudieron cargar noticias: ' + (err.message || err));
      setNewsList([]);
    } finally {
      setLoading(false);
    }
  }, [newsIdFromRoute]);

  /* --------------------- Cargar imágenes de una noticia --------------------- */
  const loadImages = useCallback(async (newsId) => {
    if (!newsId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/news/${newsId}/images`);
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Error HTTP ${res.status}: ${errorText}`);
      }
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Error cargando imágenes');

      setImages(Array.isArray(json.data) ? json.data : []);
    } catch (err) {
      setError('No se pudieron cargar imágenes: ' + (err.message || err));
      setImages([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /* --------------------- Effects --------------------- */
  useEffect(() => {
    loadNews();
  }, [loadNews]);

  useEffect(() => {
    if (selectedNewsId) {
      loadImages(selectedNewsId);
    } else {
      setImages([]);
    }
  }, [selectedNewsId, loadImages]);

  /* --------------------- Formulario --------------------- */
  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'image') {
      setFormData(prev => ({ ...prev, image: files[0] || null }));
    } else if (name === 'position') {
      setFormData(prev => ({ ...prev, position: parseInt(value === '' ? 0 : value, 10) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const selectNews = (newsId) => {
    setSelectedNewsId(String(newsId));
    setShowForm(false);
    setEditingId(null);
    setFormData({ image: null, caption: '', alt_text: '', position: 0 });

    // ajustar página si la noticia seleccionada no está en la página actual
    const idx = newsList.findIndex(n => String(n.id) === String(newsId));
    if (idx >= 0) {
      const targetPage = Math.floor(idx / NEWS_PAGE_SIZE);
      setPage(targetPage);
    }
  };

  // Nueva función: deseleccionar noticia y volver al estado inicial del panel
  const deselectNews = () => {
    setSelectedNewsId('');
    setImages([]);
    setShowForm(false);
    setEditingId(null);
    setFormData({ image: null, caption: '', alt_text: '', position: 0 });
    // opcional: volver a la primera página de la lista
    setPage(0);
  };

  /* --------------------- Crear (subir) imagen --------------------- */
  const handleCreateOrUpdate = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!selectedNewsId) {
      setError('Selecciona primero una noticia.');
      return;
    }

    if (editingId) {
      await updateMetadata(editingId);
      return;
    }

    if (!formData.image) {
      setError('Selecciona un archivo de imagen.');
      return;
    }

    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('image', formData.image);
      fd.append('caption', formData.caption || '');
      fd.append('alt_text', formData.alt_text || '');
      fd.append('position', formData.position.toString());

      const res = await fetch(`${API_URL}/news/${selectedNewsId}/images`, {
        method: 'POST',
        body: fd
      });

      const responseText = await res.text();
      let json;
      try { json = JSON.parse(responseText); } catch (parseError) { throw new Error(`Respuesta no válida del servidor: ${responseText.substring(0, 200)}`); }

      if (!res.ok) throw new Error(json.error || `Error HTTP ${res.status}`);
      if (!json.success) throw new Error(json.error || 'Error subiendo imagen');

      setSuccess('¡Imagen subida exitosamente!');
      setFormData({ image: null, caption: '', alt_text: '', position: 0 });
      setShowForm(false);

      setTimeout(() => loadImages(selectedNewsId), 500);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Error al subir imagen: ' + (err.message || err));
    } finally {
      setBusy(false);
    }
  };

  /* --------------------- Editar metadata --------------------- */
  const handleEdit = (img) => {
    setEditingId(img.id);
    setShowForm(true);
    setFormData({
      image: null,
      caption: img.caption || '',
      alt_text: img.alt_text || '',
      position: img.position || 0
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const updateMetadata = async (imageId) => {
    setBusy(true);
    setError(null);
    const payload = { caption: formData.caption || '', alt_text: formData.alt_text || '', position: formData.position };
    try {
      const res = await fetch(`${API_URL}/news/${selectedNewsId}/images/${imageId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      });

      const responseText = await res.text();
      let json;
      try { json = JSON.parse(responseText); } catch (parseError) { throw new Error(`Respuesta no válida: ${responseText.substring(0, 200)}`); }

      if (!res.ok) throw new Error(json.error || `Error HTTP ${res.status}`);
      if (!json.success) throw new Error(json.error || 'Error actualizando metadata');

      setSuccess('Metadata actualizada correctamente');
      setEditingId(null);
      setShowForm(false);
      setTimeout(() => loadImages(selectedNewsId), 500);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Error al actualizar: ' + (err.message || err));
    } finally {
      setBusy(false);
    }
  };

  /* --------------------- Eliminar --------------------- */
  const handleDelete = async (imageId) => {
    const ok = window.confirm('¿Estás seguro de eliminar esta imagen? Esta acción no se puede deshacer.');
    if (!ok) return;

    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/news/${selectedNewsId}/images/${imageId}`, { method: 'DELETE' });
      const responseText = await res.text();
      let json;
      try { json = JSON.parse(responseText); } catch (parseError) { throw new Error(`Respuesta no válida: ${responseText.substring(0, 200)}`); }

      if (!res.ok) throw new Error(json.error || `Error HTTP ${res.status}`);
      if (!json.success) throw new Error(json.error || 'Error al eliminar');

      setSuccess('Imagen eliminada correctamente');
      setTimeout(() => loadImages(selectedNewsId), 500);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Error al eliminar: ' + (err.message || err));
    } finally {
      setBusy(false);
    }
  };

  /* --------------------- PAGINACIÓN (noticias) --------------------- */
  const totalPages = Math.max(1, Math.ceil(newsList.length / NEWS_PAGE_SIZE));
  const start = page * NEWS_PAGE_SIZE;
  const pageItems = newsList.slice(start, start + NEWS_PAGE_SIZE);

  const handlePrev = () => setPage(p => Math.max(0, p - 1));
  const handleNext = () => setPage(p => Math.min(totalPages - 1, p + 1));

  const rangeStart = newsList.length === 0 ? 0 : start + 1;
  const rangeEnd = Math.min(start + NEWS_PAGE_SIZE, newsList.length);

  /* --------------------- Render --------------------- */
  return (
    <div className="home-container">
      <AdminNavbar />

      {/* Contenido principal */}
      <main className="main-content" style={{ padding: 20 }}>
        {/* Page header específico (antes era el header propio) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
          <img src={zmLogo} alt="ZM Logo" style={{ height: 40 }} />
          <div>
            <h2 style={{ margin: 0, fontSize: 20, color: '#333' }}>Gestión de Imágenes</h2>
            <p style={{ margin: 0, fontSize: 13, color: '#666' }}>Selecciona una noticia y administra sus imágenes</p>
          </div>
        </div>

        {/* Mensajes de estado */}
        {error && (
          <div className="alert alert-error" style={{
            marginBottom: 12, padding: '12px 16px',
            backgroundColor: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb', borderRadius: 4
          }}>
            <strong>Error:</strong> {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success" style={{
            marginBottom: 12, padding: '12px 16px',
            backgroundColor: '#d4edda', color: '#155724', border: '1px solid #c3e6cb', borderRadius: 4
          }}>
            <strong>Éxito:</strong> {success}
          </div>
        )}

        {/* Sección de noticias */}
        <section style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
            <h3 style={{ margin: 0, fontSize: 18, color: '#333' }}>
              Noticias Disponibles {newsList.length > 0 && `(${newsList.length})`}
            </h3>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                className="btn btn-secondary"
                onClick={() => loadNews()}
                disabled={loading}
                style={{
                  padding: '8px 16px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: 4,
                  cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1
                }}
              >
                {loading ? 'Cargando...' : '↻ Refrescar'}
              </button>

              {/* PAGINADOR de noticias */}
              {newsList.length > NEWS_PAGE_SIZE && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 8, flexWrap: 'wrap' }}>
                  <button onClick={handlePrev} disabled={page === 0} className="page-btn" style={{ padding: '6px 10px' }}>
                    &lt;
                  </button>
                  <div style={{ fontSize: 13, color: '#444', textAlign: 'center', flex: '1 1 auto', minWidth: 0 }}>
                    Noticias {rangeStart}-{rangeEnd} de {newsList.length} (pág. {page + 1}/{totalPages})
                  </div>
                  <button onClick={handleNext} disabled={page >= totalPages - 1} className="page-btn" style={{ padding: '6px 10px' }}>
                    &gt;
                  </button>
                </div>
              )}
            </div>
          </div>

          {newsList.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', backgroundColor: '#f8f9fa', borderRadius: 4, border: '1px dashed #dee2e6' }}>
              {loading ? 'Cargando noticias...' : 'No hay noticias disponibles.'}
            </div>
          ) : (
            <div style={{ overflowX: 'auto', borderRadius: 4, border: '1px solid #dee2e6' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead style={{ backgroundColor: '#f8f9fa' }}>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '12px', borderBottom: '1px solid #dee2e6' }}>ID</th>
                    <th style={{ textAlign: 'left', padding: '12px', borderBottom: '1px solid #dee2e6' }}>Título</th>
                    <th style={{ textAlign: 'left', padding: '12px', borderBottom: '1px solid #dee2e6' }}>Autor</th>
                    <th style={{ textAlign: 'left', padding: '12px', borderBottom: '1px solid #dee2e6' }}>Categoría</th>
                    <th style={{ textAlign: 'left', padding: '12px', borderBottom: '1px solid #dee2e6' }}>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map(n => (
                    <tr key={n.id} style={{
                      backgroundColor: selectedNewsId === String(n.id) ? '#e7f3ff' : 'transparent',
                      borderBottom: '1px solid #f0f0f0'
                    }}>
                      <td style={{ padding: '12px', fontWeight: 'bold' }}>{n.id}</td>
                      <td style={{ padding: '12px', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>{n.title || 'Sin título'}</td>
                      <td style={{ padding: '12px' }}>{n.authors?.name || n.author_name || '-'}</td>
                      <td style={{ padding: '12px' }}>{n.categories?.name || n.category_name || '-'}</td>
                      <td style={{ padding: '12px' }}>
                        <button
                          onClick={() => selectNews(n.id)}
                          disabled={busy}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: selectedNewsId === String(n.id) ? '#0d6efd' : '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: 4,
                            cursor: busy ? 'not-allowed' : 'pointer',
                            opacity: busy ? 0.6 : 1
                          }}
                        >
                          {selectedNewsId === String(n.id) ? '✓ Seleccionada' : 'Seleccionar'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Sección de imágenes */}
        <section>
          {selectedNewsId ? (
            <>
              {/* Encabezado de imágenes */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
                <h4 style={{ margin: 0, fontSize: 18, color: '#333' }}>
                  Imágenes de la Noticia #{selectedNewsId} {images.length > 0 && `(${images.length})`}
                </h4>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button onClick={() => setShowForm(!showForm)} style={{
                    padding: '8px 16px', backgroundColor: '#0d6efd', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer'
                  }}>
                    {showForm ? '← Cancelar' : '+ Subir Imagen'}
                  </button>

                  <button onClick={() => loadImages(selectedNewsId)} disabled={loading} style={{
                    padding: '8px 16px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: 4,
                    cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1
                  }}>
                    ↻ Actualizar
                  </button>

                  {/* BOTÓN CANCELAR: deselecciona la noticia y cierra el panel */}
                  <button onClick={deselectNews} style={{
                    padding: '8px 16px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: 4,
                    cursor: 'pointer'
                  }}>
                    ✕ Cancelar
                  </button>
                </div>
              </div>

              {/* Formulario de subida/edición */}
              {showForm && (
                <div style={{ marginBottom: 24, padding: 20, backgroundColor: '#f8f9fa', borderRadius: 8, border: '1px solid #dee2e6' }}>
                  <h5 style={{ marginTop: 0, marginBottom: 12 }}>{editingId ? '✏️ Editar Imagen' : '📤 Subir Nueva Imagen'}</h5>

                  <form onSubmit={handleCreateOrUpdate}>
                    {!editingId && (
                      <div style={{ marginBottom: 12 }}>
                        <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>Archivo de Imagen *</label>
                        <input type="file" name="image" accept="image/*" onChange={handleInputChange} required={!editingId} style={{ width: '100%', padding: 8, border: '1px solid #ced4da', borderRadius: 4 }} />
                        <small style={{ color: '#6c757d', display: 'block', marginTop: 6 }}>Formatos aceptados: JPG, PNG, GIF, WebP</small>
                      </div>
                    )}

                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>Título (Caption)</label>
                      <input type="text" name="caption" value={formData.caption} onChange={handleInputChange} placeholder="Ej: Presidente dando discurso" style={{ width: '100%', padding: 8, border: '1px solid #ced4da', borderRadius: 4 }} />
                    </div>

                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>Texto Alternativo (Alt Text)</label>
                      <input type="text" name="alt_text" value={formData.alt_text} onChange={handleInputChange} placeholder="Ej: Presidente dando discurso en conferencia" style={{ width: '100%', padding: 8, border: '1px solid #ced4da', borderRadius: 4 }} />
                    </div>

                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>Posición</label>
                      <input type="number" name="position" value={formData.position} onChange={handleInputChange} min="0" style={{ width: 120, padding: 8, border: '1px solid #ced4da', borderRadius: 4 }} />
                      <small style={{ color: '#6c757d', marginLeft: 8 }}>(0 = primera imagen)</small>
                    </div>

                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="submit" disabled={busy} style={{ padding: '8px 16px', backgroundColor: '#198754', color: 'white', border: 'none', borderRadius: 4, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1 }}>
                        {busy ? 'Procesando...' : (editingId ? '💾 Guardar Cambios' : '🚀 Subir Imagen')}
                      </button>

                      {editingId && (
                        <button type="button" onClick={() => { setShowForm(false); setEditingId(null); setFormData({ image: null, caption: '', alt_text: '', position: 0 }); }} style={{ padding: '8px 16px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                          Cancelar Edición
                        </button>
                      )}
                    </div>
                  </form>
                </div>
              )}

              {/* Lista de imágenes */}
              {loading ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <p>Cargando imágenes...</p>
                </div>
              ) : images.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, backgroundColor: '#f8f9fa', borderRadius: 8, border: '1px dashed #dee2e6' }}>
                  <p style={{ color: '#6c757d', marginBottom: 16 }}>No hay imágenes para esta noticia.</p>
                  <button onClick={() => setShowForm(true)} style={{ padding: '8px 16px', backgroundColor: '#0d6efd', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>+ Subir la primera imagen</button>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20, marginTop: 20 }}>
                  {images.map(img => (
                    <div key={img.id} style={{ border: '1px solid #dee2e6', borderRadius: 8, overflow: 'hidden', backgroundColor: 'white' }}>
                      <div style={{ height: 200, overflow: 'hidden', backgroundColor: '#f8f9fa' }}>
                        <img src={img.url} alt={img.alt_text || 'Imagen'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.src = 'https://via.placeholder.com/300x200?text=Imagen+no+disponible'; }} />
                      </div>

                      <div style={{ padding: 16 }}>
                        <div style={{ marginBottom: 12 }}>
                          <strong style={{ display: 'block', color: '#333', marginBottom: 4 }}>{img.caption || 'Sin título'}</strong>
                          <small style={{ color: '#6c757d' }}>Alt: {img.alt_text || 'No especificado'}</small>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 14, color: '#6c757d' }}>
                          <span>Posición: {img.position || 0}</span>
                          <span>ID: {img.id}</span>
                        </div>

                        <div style={{ display: 'flex', gap: 8, marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
                          <button onClick={() => handleEdit(img)} disabled={busy} style={{ flex: 1, padding: '8px 12px', backgroundColor: '#ffc107', color: '#212529', border: 'none', borderRadius: 4, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1 }}>
                            ✏️ Editar
                          </button>

                          <button onClick={() => handleDelete(img.id)} disabled={busy} style={{ flex: 1, padding: '8px 12px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: 4, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1 }}>
                            🗑️ Eliminar
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: 40, backgroundColor: '#f8f9fa', borderRadius: 8, border: '1px dashed #dee2e6' }}>
              <p style={{ color: '#6c757d', fontSize: 16 }}>👈 Selecciona una noticia de la lista para ver y gestionar sus imágenes</p>
            </div>
          )}
        </section>
      </main>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-links">
          <a href="#privacidad">Política de Privacidad</a>
          <a href="#terminos">Términos y Condiciones</a>
          <a href="#contacto">Contacto</a>
        </div>
        <p className="footer-text">© 2025 Panel de Administrador. Todos los derechos reservados.</p>
      </footer>
    </div>
  );
}
